import { useMemo } from "react";
import type { Color } from "@salestrak/ui";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";

/**
 * The sentinel the server emits for a NULL dimension value (an issue with no
 * stage/type/workflow/team assignee) — `issues.service.ts` groupCounts. THE one
 * shared constant both sides key on; the client must never invent its own (a
 * mismatched `"__none__"` made untyped series read as corrupt "Unknown …"). A
 * bucket carrying this key means "No <dimension>", not unknown/corrupt data.
 */
export const UNASSIGNED_DIMENSION_KEY = "__unassigned__";

/** The group-by dimensions issue-count widgets can slice on (each maps to an
 * allowed server `groupBy`). The tuple is THE shared vocabulary — every widget
 * config schema builds its `z.enum` from it; never restate the literals. */
export const WIDGET_DIMENSIONS = [
  "type",
  "workflow",
  "stage",
  "team",
  "assignee",
  "age",
] as const;
export type WidgetDimension = (typeof WIDGET_DIMENSIONS)[number];

/** One slice: a category in the chosen dimension and its scoped project count.
 * `color` is the entity-owned palette color name (stage/team/type/workflow icon
 * color, age-status deco color) — charts wear it via `useCategoricalColors`.
 * `null` means the category has no owned color (a user) and takes ramp fallback. */
export type DimensionSlice = { id: string; label: string; total: number; color?: Color | null };

/** The shared "Group by" / "Tile by" select options — one vocabulary across every
 * dimension-sliced widget's settings. */
export const WIDGET_DIMENSION_OPTIONS: { value: WidgetDimension; label: string }[] = [
  { value: "type", label: "Type" },
  { value: "workflow", label: "Workflow" },
  { value: "stage", label: "Stage" },
  { value: "team", label: "Team" },
  { value: "assignee", label: "Assignee" },
  { value: "age", label: "Age health" },
];

const GROUP_BY: Record<WidgetDimension, string> = {
  type: "issueTypeId",
  workflow: "workflowId",
  stage: "stageId",
  team: "team",
  assignee: "assignees.entityId",
  age: "age.status",
};

/**
 * Counts projects matching `where`, grouped by the chosen dimension, ranked
 * high→low. The shared data layer behind every dimension-sliced widget
 * (portfolio-map's tiles, type-mix's slices): it fans the server `groupBy`
 * through `useIssueGroupCounts` and resolves the returned keys to display
 * labels via the workspace maps (an assignee may be a user OR a team, so both
 * are tried). `__unassigned__` is dropped — these charts show the categorized
 * work. The caller owns the `where` fold (scope + its own filters).
 */
export function useDimensionCounts(
  dimension: WidgetDimension,
  where: Record<string, unknown> | undefined,
) {
  const { counts, ready, error } = useIssueGroupCounts({ where, groupBy: GROUP_BY[dimension] });
  const { stageMap, issueTypeMap, teamMap, userMap, workflowMap } = useWorkspaceData();

  const data = useMemo<DimensionSlice[]>(() => {
    const label = (id: string): string => {
      switch (dimension) {
        case "type":
          return issueTypeMap.get(id)?.name ?? "Unknown";
        case "workflow":
          return workflowMap.get(id)?.name ?? "Unknown";
        case "stage":
          return stageMap.get(id)?.name ?? "Unknown";
        case "team":
          return teamMap.get(id)?.teamName ?? "Unknown";
        case "assignee":
          return userMap.get(id)?.name ?? teamMap.get(id)?.teamName ?? "Unknown";
        case "age":
          return AGE_STATUS_CONFIG[id as AgeStatus]?.label ?? "Unknown";
      }
    };
    // The stored icon color is an open string on the wire; the maps only ever
    // hold palette names, so the narrowing cast is safe.
    const color = (id: string): Color | null => {
      switch (dimension) {
        case "type":
          return (issueTypeMap.get(id)?.icon?.color as Color) ?? null;
        case "workflow":
          return (workflowMap.get(id)?.icon?.color as Color) ?? null;
        case "stage":
          return (stageMap.get(id)?.icon?.color as Color) ?? null;
        case "team":
          return (teamMap.get(id)?.icon?.color as Color) ?? null;
        case "assignee":
          // A user assignee has no owned color (avatars, not icons); a team
          // assignee wears its team color.
          return userMap.has(id) ? null : ((teamMap.get(id)?.icon?.color as Color) ?? null);
        case "age":
          return AGE_STATUS_CONFIG[id as AgeStatus]?.decoColor ?? null;
      }
    };
    return Object.entries(counts)
      .filter(([id]) => id !== UNASSIGNED_DIMENSION_KEY)
      .map(([id, total]) => ({ id, total, label: label(id), color: color(id) }))
      .sort((a, b) => b.total - a.total);
  }, [counts, dimension, stageMap, issueTypeMap, teamMap, userMap, workflowMap]);

  return { data, loading: !ready, error };
}
